import type { Inquiry } from '@/lib/supabase';

const categoryLabel: Record<string, string> = {
  inquiry: '문의',
  suggestion: '건의사항',
};

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function InquiryList({
  inquiries,
  loading,
}: {
  inquiries: Inquiry[];
  loading: boolean;
}) {
  return (
    <div className="bg-white rounded-xl border border-background-200">
      <div className="flex items-center gap-2 px-5 md:px-6 pt-5 pb-3 border-b border-background-100">
        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
          <i className="ri-mail-check-line text-lg"></i>
        </div>
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">내 문의 내역</h2>
          <p className="text-xs text-foreground-500">총 {inquiries.length}건</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : inquiries.length === 0 ? (
        <div className="p-8 text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
            <i className="ri-chat-smile-2-line text-2xl"></i>
          </div>
          <p className="text-sm text-foreground-500">아직 문의 내역이 없습니다.</p>
          <p className="text-xs text-foreground-400 mt-1">
            궁금한 점이나 개선 의견을 자유롭게 남겨주세요.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-background-100">
          {inquiries.map((inq) => {
            const isAnswered = inq.status === 'answered';
            return (
              <div key={inq.id} className="p-5 md:p-6">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        inq.category === 'suggestion'
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-primary-100 text-primary-700'
                      }`}
                    >
                      {categoryLabel[inq.category] || '문의'}
                    </span>
                    <h3 className="font-semibold text-foreground-950 text-sm md:text-base break-words">
                      {inq.title}
                    </h3>
                  </div>
                  <span
                    className={`whitespace-nowrap shrink-0 text-xs px-2 py-1 rounded-full ${
                      isAnswered
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {isAnswered ? '답변 완료' : '답변 대기'}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-wrap break-words">
                  {inq.content}
                </p>
                <div className="text-xs text-foreground-400 mt-2">
                  {formatDate(inq.created_at)}
                </div>

                {isAnswered && inq.response && (
                  <div className="mt-3 rounded-lg bg-background-50 border border-background-200 p-4">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <i className="ri-reply-line text-primary-600"></i>
                      <span className="text-xs font-semibold text-foreground-900">운영자 답변</span>
                      {inq.responded_at && (
                        <span className="text-xs text-foreground-400">
                          · {formatDate(inq.responded_at)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-wrap break-words">
                      {inq.response}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}